// @ts-check

/**
 * <collection-navigation>
 *
 * Horizontally scrollable row of category tiles with a custom scroll bar.
 * The native scrollbar is hidden via CSS; this element drives a thumb whose
 * width and position reflect the scroll progress. The thumb is also
 * draggable, and clicking the track jumps to that position.
 */
class CollectionNavigation extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLElement | null} */
    this.scroller = this.querySelector('.collection-navigation__scroller');
    /** @type {HTMLElement | null} */
    this.bar = this.querySelector('.collection-navigation__bar');
    /** @type {HTMLElement | null} */
    this.thumb = this.querySelector('.collection-navigation__thumb');

    if (!this.scroller || !this.bar || !this.thumb) return;

    this._ticking = false;
    this._dragging = false;

    this._onScroll = () => this.requestUpdate();
    this._onResize = () => this.update();
    this._onThumbPointerDown = (e) => this.startDrag(e);
    this._onTrackPointerDown = (e) => this.jumpToTrack(e);
    this._onPointerMove = (e) => this.onDrag(e);
    this._onPointerUp = () => this.endDrag();

    this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);
    this.thumb.addEventListener('pointerdown', this._onThumbPointerDown);
    this.bar.addEventListener('pointerdown', this._onTrackPointerDown);

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => this.update());
      this._ro.observe(this.scroller);
    }

    // Initial paint after layout/fonts settle.
    requestAnimationFrame(() => this.update());

    // Bring the active tile into view on load.
    requestAnimationFrame(() => this.scrollActiveIntoView());
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    this.thumb?.removeEventListener('pointerdown', this._onThumbPointerDown);
    this.bar?.removeEventListener('pointerdown', this._onTrackPointerDown);
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    this._ro?.disconnect();
  }

  requestUpdate() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._ticking = false;
      this.update();
    });
  }

  /** Recompute thumb size/position and toggle bar visibility. */
  update() {
    if (!this.scroller || !this.bar || !this.thumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
    const maxScroll = scrollWidth - clientWidth;

    // No overflow → nothing to scroll, hide the bar.
    if (maxScroll <= 1) {
      this.bar.hidden = true;
      return;
    }
    this.bar.hidden = false;

    const trackWidth = this.bar.clientWidth;
    const ratio = clientWidth / scrollWidth;
    // Clamp the thumb between 24px and 112px. A smaller cap means the thumb
    // travels further (and faster) across the track for the same scroll.
    const thumbWidth = Math.min(112, Math.max(24, Math.round(trackWidth * ratio)));
    const travel = trackWidth - thumbWidth;
    const progress = scrollLeft / maxScroll;
    const thumbX = Math.round(progress * travel);

    this.style.setProperty('--cnav-thumb-width', `${thumbWidth}px`);
    this.style.setProperty('--cnav-thumb-x', `${thumbX}px`);
  }

  /**
   * @param {PointerEvent} e
   */
  startDrag(e) {
    if (!this.scroller || !this.bar || !this.thumb) return;
    e.preventDefault();
    e.stopPropagation();

    this._dragging = true;
    this._dragStartX = e.clientX;
    this._dragStartScrollLeft = this.scroller.scrollLeft;
    this.thumb.style.cursor = 'grabbing';

    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp, { once: true });
  }

  /**
   * @param {PointerEvent} e
   */
  onDrag(e) {
    if (!this._dragging || !this.scroller || !this.bar || !this.thumb) return;

    const { scrollWidth, clientWidth } = this.scroller;
    const maxScroll = scrollWidth - clientWidth;
    const trackWidth = this.bar.clientWidth;
    const thumbWidth = this.thumb.clientWidth;
    const travel = trackWidth - thumbWidth;
    if (travel <= 0) return;

    const deltaX = e.clientX - this._dragStartX;
    const scrollDelta = (deltaX / travel) * maxScroll;
    this.scroller.scrollLeft = this._dragStartScrollLeft + scrollDelta;
  }

  endDrag() {
    this._dragging = false;
    if (this.thumb) this.thumb.style.cursor = 'grab';
    window.removeEventListener('pointermove', this._onPointerMove);
  }

  /**
   * Click on the track (not the thumb) to jump near that position.
   * @param {PointerEvent} e
   */
  jumpToTrack(e) {
    if (e.target === this.thumb || !this.scroller || !this.bar) return;

    const { scrollWidth, clientWidth } = this.scroller;
    const maxScroll = scrollWidth - clientWidth;
    const rect = this.bar.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    this.scroller.scrollTo({ left: ratio * maxScroll, behavior: 'smooth' });
  }

  scrollActiveIntoView() {
    if (!this.scroller) return;
    const active = this.scroller.querySelector('[aria-current="true"]');
    if (!(active instanceof HTMLElement)) return;

    const offset = active.offsetLeft - (this.scroller.clientWidth - active.clientWidth) / 2;
    this.scroller.scrollTo({ left: Math.max(0, offset), behavior: 'instant' });
  }
}

if (!customElements.get('collection-navigation')) {
  customElements.define('collection-navigation', CollectionNavigation);
}
